import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SubscriptionSection from '../components/SubscriptionSection';
import '../styles/Jobs.css';

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedJobType, setSelectedJobType] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // API configuration
  const API_BASE_URL = "https://phucansolutions.vn" ;

  // Fetch jobs data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log('🔄 Fetching jobs data...');
        
        // Fetch jobs data
        const jobsResponse = await fetch(`${API_BASE_URL}/api/jobs?limit=50`);

        if (!jobsResponse.ok) {
          throw new Error(`API error: ${jobsResponse.status}`);
        }

        const jobsData = await jobsResponse.json();

        console.log('✅ Jobs data fetched:', jobsData);

        if (jobsData.success && jobsData.data) {
          setJobs(jobsData.data);
          
          // Extract unique departments from jobs
          const uniqueDepartments = [...new Set(jobsData.data.map(job => job.department))];
          const departmentCounts = uniqueDepartments.map(dept => ({
            id: dept,
            name: getDepartmentDisplayName(dept),
            count: jobsData.data.filter(job => job.department === dept).length
          }));

          setDepartments([
            { id: 'all', name: 'Tất cả phòng ban', count: jobsData.data.length },
            ...departmentCounts
          ]);
        } else {
          setJobs([]);
          setDepartments([]);
        }
        
      } catch (err) {
        console.error('❌ Error fetching jobs:', err);
        setError(err.message);
        
        // Use fallback data
        setJobs(getFallbackJobs());
        setDepartments(getFallbackDepartments());
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API_BASE_URL]);

  // Get department display name
  const getDepartmentDisplayName = (dept) => {
    const departmentMap = {
      'engineering': 'Kỹ thuật',
      'marketing': 'Marketing',
      'sales': 'Kinh doanh',
      'hr': 'Nhân sự',
      'finance': 'Tài chính',
      'operations': 'Vận hành'
    };
    return departmentMap[dept] || dept;
  };

  // Get job type display name
  const getJobTypeDisplayName = (type) => {
    const typeMap = {
      'full-time': 'Toàn thời gian',
      'part-time': 'Bán thời gian',
      'contract': 'Hợp đồng',
      'internship': 'Thực tập'
    };
    return typeMap[type] || type;
  };

  // Get experience level display name
  const getExperienceLevelDisplayName = (level) => {
    const levelMap = {
      'entry': 'Mới tốt nghiệp',
      'junior': 'Nhân viên',
      'mid': 'Trung cấp',
      'senior': 'Cao cấp',
      'lead': 'Trưởng nhóm',
      'manager': 'Quản lý'
    };
    return levelMap[level] || level;
  };

  // Filter and sort jobs
  const filteredJobs = jobs.filter(job => {
    const matchesDepartment = selectedDepartment === 'all' || job.department === selectedDepartment;
    const matchesJobType = selectedJobType === 'all' || job.job_type === selectedJobType;
    const matchesLocation = selectedLocation === 'all' || job.location.toLowerCase().includes(selectedLocation.toLowerCase());
    const matchesSearch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (job.summary && job.summary.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         (job.description && job.description.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesDepartment && matchesJobType && matchesLocation && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'salary':
        return (b.max_salary || 0) - (a.max_salary || 0);
      case 'title':
        return a.title.localeCompare(b.title);
      default:
        return 0;
    }
  });

  // Format salary
  const formatSalary = (min, max) => {
    if (!min && !max) return 'Thỏa thuận';
    
    const formatNumber = (num) => {
      if (num >= 1000000) {
        return (num / 1000000) + ' triệu';
      }
      return num.toLocaleString() + ' VND';
    };

    if (min && max) {
      return `${formatNumber(min)} - ${formatNumber(max)}`;
    }
    return formatNumber(min || max);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Get job image URL (matching News.jsx pattern)
  const getJobImageUrl = (job) => {
    const image = job.image;
    if (!image || image === '/images') {
      return '/images';
    }
    
    // If image starts with /uploads, add API base URL
    if (image.startsWith('/uploads/')) {
      return `${API_BASE_URL}${image}`;
    }
    
    return image;
  };

  // Reset filters
  const resetFilters = () => {
    setSelectedDepartment('all');
    setSelectedJobType('all');
    setSelectedLocation('all');
    setSearchTerm('');
    setSortBy('newest');
  };

  // Get unique job types and locations from current jobs
  const getUniqueJobTypes = () => {
    const types = [...new Set(jobs.map(job => job.job_type))];
    return types.map(type => ({ value: type, label: getJobTypeDisplayName(type) }));
  };

  const getUniqueLocations = () => {
    return [...new Set(jobs.map(job => job.location))];
  };

  // Fallback data
  const getFallbackJobs = () => [
    {
      id: 1,
      title: 'Senior Frontend Developer',
      summary: 'Phát triển ứng dụng web hiện đại với React và TypeScript',
      department: 'engineering',
      job_type: 'full-time',
      experience_level: 'senior',
      min_salary: 20000000,
      max_salary: 30000000,
      location: 'Ho Chi Minh City',
      remote_allowed: true,
      featured: true,
      positions_available: 2,
      application_deadline: '2024-03-15',
      created_at: '2024-01-15',
      image: '/images/jobs/frontend-developer.jpg'
    },
    {
      id: 2,
      title: 'IoT Solutions Engineer',
      summary: 'Phát triển giải pháp IoT cho smart home và smart building',
      department: 'engineering',
      job_type: 'full-time',
      experience_level: 'mid',
      min_salary: 15000000,
      max_salary: 25000000,
      location: 'Ho Chi Minh City',
      remote_allowed: false,
      featured: true,
      positions_available: 1,
      application_deadline: '2024-02-28',
      created_at: '2024-01-10',
      image: '/images/jobs/iot-engineer.jpg'
    }
  ];

  const getFallbackDepartments = () => [
    { id: 'all', name: 'Tất cả phòng ban', count: 2 },
    { id: 'engineering', name: 'Kỹ thuật', count: 2 }
  ];

  // Job Card Skeleton
  const JobCardSkeleton = () => (
    <div className="job-card skeleton">
      <div className="job-header-skeleton">
        <div className="skeleton-line"></div>
        <div className="skeleton-line short"></div>
      </div>
      <div className="job-content-skeleton">
        <div className="skeleton-line"></div>
        <div className="skeleton-line"></div>
        <div className="skeleton-line short"></div>
      </div>
    </div>
  );

  return (
    <div className="jobs-main">
      {/* Hero Section */}
      <section className="jobs-hero">
        <img 
          src="/images/career-banner.jpg" 
          alt="Cơ hội nghề nghiệp tại Phu Cần" 
          className="hero-image"
          onError={(e) => {
            e.target.src = '/images/placeholder-banner.jpg';
          }}
        />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <h1>Cơ Hội Nghề Nghiệp</h1>
          <p>Tham gia đội ngũ chuyên gia công nghệ, cùng phát triển những giải pháp thông minh cho tương lai</p>
        </div>
      </section>

      <div className="jobs-container">
        {/* Filters Section */}
        <section className="jobs-filters">
          <div className="filters-row">
            {/* Search Box */}
            <div className="search-box">
              <input
                type="text"
                placeholder="Tìm kiếm vị trí công việc..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <i className="fas fa-search search-icon"></i>
            </div>

            {/* Job Type Filter */}
            <div className="filter-box">
              <select
                value={selectedJobType}
                onChange={(e) => setSelectedJobType(e.target.value)}
                className="filter-select"
              >
                <option value="all">Tất cả loại hình</option>
                {getUniqueJobTypes().map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Location Filter */}
            <div className="filter-box">
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="filter-select"
              >
                <option value="all">Tất cả địa điểm</option>
                {getUniqueLocations().map(location => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort Box */}
            <div className="sort-box">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="sort-select"
              >
                <option value="newest">Mới nhất</option>
                <option value="oldest">Cũ nhất</option>
                <option value="salary">Lương cao nhất</option>
                <option value="title">Theo tên</option>
              </select>
            </div>
          </div>

          {/* Department Categories */}
          <div className="departments-row">
            {departments.map(dept => (
              <button
                key={dept.id}
                onClick={() => setSelectedDepartment(dept.id)}
                className={`department-btn ${selectedDepartment === dept.id ? 'active' : ''}`}
              >
                {dept.name}
                <span className="count">{dept.count}</span>
              </button>
            ))}
          </div>
        </section>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <p>Không thể tải dữ liệu việc làm. Đang hiển thị dữ liệu mẫu.</p>
          </div>
        )}

        {/* Jobs Grid */}
        <section className="jobs-grid">
          {loading ? (
            // Loading Skeletons
            Array.from({ length: 6 }, (_, index) => (
              <JobCardSkeleton key={index} />
            ))
          ) : filteredJobs.length > 0 ? (
            // Job Cards
            filteredJobs.map(job => (
              <article key={job.id} className="job-card">
                {/* Job Image */}
                <div className="job-image">
                  <img 
                    src={getJobImageUrl(job)} 
                    alt={`Tuyển dụng ${job.title}`}
                    onError={(e) => {
                      e.target.src = '/images';
                    }}
                  />
                  <div className="job-badges">
                    <span className="department-badge">
                      {getDepartmentDisplayName(job.department)}
                    </span>
                    {job.remote_allowed && (
                      <span className="remote-badge">Remote</span>
                    )}
                  </div>
                </div>

                <div className="job-header">
                  <h3 className="job-title">{job.title}</h3>
                  <p className="job-summary">{job.summary}</p>
                </div>

                <div className="job-details">
                  <div className="detail-item">
                    <span className="detail-label">Loại hình:</span>
                    <span className="detail-value">{getJobTypeDisplayName(job.job_type)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Kinh nghiệm:</span>
                    <span className="detail-value">{getExperienceLevelDisplayName(job.experience_level)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Địa điểm:</span>
                    <span className="detail-value">
                      {job.location}
                      {job.remote_allowed && <span className="remote-tag">Remote OK</span>}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Mức lương:</span>
                    <span className="detail-value salary">{formatSalary(job.min_salary, job.max_salary)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Số lượng:</span>
                    <span className="detail-value">{job.positions_available} vị trí</span>
                  </div>
                  {job.application_deadline && (
                    <div className="detail-item">
                      <span className="detail-label">Hạn nộp:</span>
                      <span className="detail-value deadline">{formatDate(job.application_deadline)}</span>
                    </div>
                  )}
                </div>

                <div className="job-actions">
                  <button className="job-btn primary">
                    <i className="fas fa-paper-plane"></i>
                    Ứng tuyển ngay
                  </button>
                  <Link to={`/jobs/${job.slug || job.id}`} className="job-btn secondary">
                    <i className="fas fa-info-circle"></i>
                    Chi tiết
                  </Link>
                </div>
              </article>
            ))
          ) : (
            // No Jobs Found
            <div className="no-jobs">
              <div className="no-jobs-content">
                <i className="fas fa-briefcase"></i>
                <h3>Không tìm thấy việc làm phù hợp</h3>
                <p>Hãy thử điều chỉnh bộ lọc hoặc từ khóa tìm kiếm của bạn.</p>
                <button onClick={resetFilters} className="reset-filters-btn">
                  Đặt lại bộ lọc
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Jobs Stats */}
        {!loading && filteredJobs.length > 0 && (
          <section className="jobs-stats">
            <p>Hiển thị {filteredJobs.length} / {jobs.length} việc làm</p>
          </section>
        )}
      </div>
      
      {/* Subscription Section */}
      <SubscriptionSection />
    </div>
  );
};

export default Jobs; 